import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import LoadingSpinner from 'common/components/LoadingSpinner';
import TeamLeaderLeaderboardModal from './TeamLeaderLeaderboardModal';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { Icon, SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
import { useGetTeamSnapshotLeadersQuery } from '../api/teamLeaderboardApi';

const badgeDefinitions: Record<
  string,
  {
    icon: SemanticICONS;
    color: SemanticCOLORS;
    label: string;
    description: string;
    borderColor: string;
    backgroundColor: string;
    metricKey?: string;
    threshold?: number;
  }
> = {
  last_login_in_period: {
    icon: 'sign in alternate',
    color: 'purple',
    label: 'Engaging Leader',
    description: 'Logged in since the last assessment!',
    borderColor: '#8b5cf6',
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    threshold: 1,
  },
  video_watches: {
    icon: 'play circle',
    color: 'blue',
    label: 'Video nuTuber',
    description: 'Watched at least one video resource!',
    borderColor: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
  },
  pdf_opens: {
    icon: 'file pdf',
    color: 'red',
    label: 'Doc Rockstar',
    description: 'Viewed at least one PDF resource!',
    borderColor: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
  },
  file_share_views: {
    icon: 'eye',
    color: 'blue',
    label: 'Share Viewer',
    description: 'A shared resource you sent was viewed by a recipient!',
    borderColor: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
  },
  knowledge_base_shares: {
    icon: 'share',
    color: 'teal',
    label: 'Resource Sharer',
    description: 'Shared a resource from the knowledge base!',
    borderColor: '#14b8a6',
    backgroundColor: 'rgba(20, 184, 166, 0.12)',
  },
  link_opens: {
    icon: 'external alternate',
    color: 'violet',
    label: 'Link Explorer',
    description: 'Viewed at least one link resource!',
    borderColor: '#7c3aed',
    backgroundColor: 'rgba(124, 58, 237, 0.12)',
  },
  focus_plan_interventions: {
    icon: 'clipboard list',
    color: 'green',
    label: 'Focus Planner',
    description: 'Created a focus plan!',
    borderColor: '#22c55e',
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
  },
  action_hero: {
    icon: 'star',
    color: 'orange',
    label: 'Action Hero',
    description: 'Completed at least 5 culture actions!',
    borderColor: '#f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    metricKey: 'focus_plan_culture_actions',
    threshold: 5,
  },
};

const streakBadgeDefinitions: Array<{
  icon: SemanticICONS;
  color: SemanticCOLORS;
  label: string;
  description: string;
  borderColor: string;
  backgroundColor: string;
  threshold: number;
}> = [
    {
      icon: 'trophy',
      color: 'yellow',
      label: 'Streak Champion',
      description: 'Maintained a 20+ week participation streak!',
      borderColor: '#d97706',
      backgroundColor: 'rgba(251, 191, 36, 0.12)',
      threshold: 20,
    },
    {
      icon: 'star',
      color: 'teal',
      label: 'Streak Pro',
      description: 'Maintained a 10+ week participation streak!',
      borderColor: '#0d9488',
      backgroundColor: 'rgba(20, 184, 166, 0.12)',
      threshold: 10,
    },
    {
      icon: 'fire',
      color: 'red',
      label: 'Streaker',
      description: 'Maintained a 5+ week participation streak!',
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.12)',
      threshold: 5,
    },
  ];

const TeamLeaderLeaderboard: React.FC = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const { setTopBarContent } = useContext(TopBarContext);
  const [selectedLeader, setSelectedLeader] = useState<any>(null);

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent]);

  const { data: leaders, isLoading, isFetching, error } = useGetTeamSnapshotLeadersQuery(
    { assessmentPeriodId },
    {
      skip: !assessmentPeriodId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const leaderRows = leaders || [];
  const loading = isLoading || isFetching;

  const sortedLeaderRows = useMemo(
    () =>
      [...leaderRows].sort((a, b) => {
        const scoreA = a.score ?? 0;
        const scoreB = b.score ?? 0;
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }

        const nameA = `${a.leader_first_name || ''} ${a.leader_last_name || ''}`.trim();
        const nameB = `${b.leader_first_name || ''} ${b.leader_last_name || ''}`.trim();
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      }),
    [leaderRows]
  );

  const getLeaderIcon = (index: number, score?: number) =>
    index < 3 && score && score > 0 ? 'trophy' : 'user';

  const getLeaderIconColor = (index: number, score?: number) => {
    if (index < 3 && score && score > 0) {
      if (index === 0) return '#fbbf24';
      if (index === 1) return '#6b7280';
      if (index === 2) return '#c2410c';
      return '#60a5fa';
    }

    return score && score > 0 ? '#60a5fa' : '#9ca3af';
  };

  const getLeaderIconBackground = (index: number, score?: number) => {
    if (index < 3 && score && score > 0) {
      if (index === 0) return 'rgba(253, 230, 138, 0.35)';
      if (index === 1) return 'rgba(229, 231, 235, 0.5)';
      if (index === 2) return 'rgba(254, 215, 170, 0.35)';
      return 'transparent';
    }

    return score && score > 0 ? 'rgba(96, 165, 250, 0.12)' : 'rgba(156, 163, 175, 0.12)';
  };

  const formatStreakValue = (leader: any) => {
    const streak = leader.score_components?.participation_streak_weeks ?? 0;
    return streak > 0 ? streak : '-';
  };

  const renderLeaderBadges = (leader: any) => {
    const streak = leader.score_components?.participation_streak_weeks ?? 0;
    const streakBadge = streakBadgeDefinitions.find(badge => streak >= badge.threshold);

    return (
      <>
        {streakBadge ? (
          <BadgeIcon
            key="streak"
            aria-label={`${streakBadge.label}: ${streakBadge.description}`}
            tabIndex={0}
            color={streakBadge.color}
            borderColor={streakBadge.borderColor}
            backgroundColor={streakBadge.backgroundColor}
          >
            <Icon name={streakBadge.icon} color={streakBadge.color} size="large" />
            <BadgeTooltip>
              <BadgeTooltipTitle>{streakBadge.label}</BadgeTooltipTitle>
              <BadgeTooltipCaption>{streakBadge.description}</BadgeTooltipCaption>
            </BadgeTooltip>
          </BadgeIcon>
        ) : null}
        {Object.entries(badgeDefinitions).map(([key, badge]) => {
          const metricKey = badge.metricKey || key;
          const count = leader.score_components?.[metricKey] || 0;
          const threshold = badge.threshold ?? 1;
          return count >= threshold ? (
            <BadgeIcon
              key={key}
              aria-label={`${badge.label}: ${badge.description}`}
              tabIndex={0}
              color={badge.color}
              borderColor={badge.borderColor}
              backgroundColor={badge.backgroundColor}
            >
              <Icon name={badge.icon} color={badge.color} size="large" />
              <BadgeTooltip>
                <BadgeTooltipTitle>{badge.label}</BadgeTooltipTitle>
                <BadgeTooltipCaption>{badge.description}</BadgeTooltipCaption>
              </BadgeTooltip>
            </BadgeIcon>
          ) : null;
        })}
      </>
    );
  };

  return (
    <PageContent>
    <PageSection>
      <PageHeader title="Team Leaderboard"
        caption='Earn points by participating in activities that strengthen your leadership and support your team.'
      />

      {!assessmentPeriodId ? (
        <MessageContainer>
          <MessageHeading>Please select an assessment period to view team leaders.</MessageHeading>
        </MessageContainer>
      ) : loading ? (
        <LoadingSpinner />
      ) : error ? (
        <MessageContainer>
          <MessageHeading>Unable to load leaderboard data.</MessageHeading>
        </MessageContainer>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <HeaderCell width={3}>Leader</HeaderCell>
              <HeaderCell width={2} justify="center">Week Streak</HeaderCell>
              <HeaderCell width={5}>Accomplishments</HeaderCell>
              <HeaderCell width={2} justify="center">Score</HeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLeaderRows.length > 0 ? (
              sortedLeaderRows.map((leader, index) => (
                <TableRow
                  key={leader.leader_id}
                  $selectable
                  onClick={() => setSelectedLeader(leader)}
                >
                  <TableCell $firstcol>
                    <NameCell>
                      <TopTrophyWrapper
                        backgroundColor={getLeaderIconBackground(index, leader.score ?? 0)}
                        borderColor={getLeaderIconColor(index, leader.score ?? 0)}
                      >
                        <Icon
                          name={getLeaderIcon(index, leader.score ?? 0)}
                          style={
                            getLeaderIcon(index, leader.score ?? 0) === 'user'
                              ? {
                                color: getLeaderIconColor(index, leader.score ?? 0),
                                fontSize: '24px',
                                transform: 'translateX(2px)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1,
                              }
                              : {
                                color: getLeaderIconColor(index, leader.score ?? 0),
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1,
                                transform: 'translateX(2px)',
                              }
                          }
                          size={getLeaderIcon(index, leader.score ?? 0) === 'user' ? undefined : 'big'}
                        />
                      </TopTrophyWrapper>
                      <NameText $hasEmail={Boolean(leader.leader_email)}>
                        <span>{leader.leader_first_name || ''} {leader.leader_last_name || ''}</span>
                        {leader.leader_email ? <EmailText>{leader.leader_email}</EmailText> : null}
                      </NameText>
                    </NameCell>
                  </TableCell>
                  <TableCell $firstcol justify="center">
                    {formatStreakValue(leader)}
                  </TableCell>
                  <TableCell>
                    <BadgeStrip>{renderLeaderBadges(leader)}</BadgeStrip>
                  </TableCell>
                  <TableCell justify="center" $firstcol>{leader.score ?? 0}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  No team leaders were found for the selected assessment period.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <TeamLeaderLeaderboardModal
        selectedLeader={selectedLeader}
        open={Boolean(selectedLeader)}
        onClose={() => setSelectedLeader(null)}
      />
    </PageSection>
    </PageContent>
  );
};

export default TeamLeaderLeaderboard;

const MessageContainer = styled.div`
  padding: 24px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-top: 16px;
`;

const MessageHeading = styled.h2`
  margin: 0;
  font-size: 16px;
  color: #374151;
`;

const NameCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  svg {
    flex-shrink: 0;
  }
`;

const NameText = styled.div<{ $hasEmail: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1px;
  justify-content: ${props => (props.$hasEmail ? 'flex-start' : 'center')};
  height: 100%;
`;

const EmailText = styled.span`
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.2;
`;

const BadgeStrip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const BadgeIcon = styled.span<{ color: string; borderColor: string; backgroundColor: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid ${props => props.borderColor};
  background: ${props => props.backgroundColor};
  color: ${props => props.color};
  cursor: default;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  i.icon,
  svg {
    width: 20px;
    height: 20px;
    font-size: 20px;
    margin-left: 2px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.18);
  }

  &:hover > div,
  &:focus-within > div {
    visibility: visible;
    opacity: 1;
    transform: translate(-50%, 4px);
  }
`;

const BadgeTooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  z-index: 20;
  min-width: 160px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);
  color: #0f172a;
  visibility: hidden;
  opacity: 0;
  transform: translate(-50%, 0);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
  white-space: normal;
  text-align: left;
  pointer-events: none;
`;

const BadgeTooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 4px;
  color: #111827;
`;

const BadgeTooltipCaption = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: #475569;
`;

const TopTrophyWrapper = styled.span<{ backgroundColor: string; borderColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${props => props.backgroundColor};
  border: 1px solid ${props => props.borderColor};
  line-height: 0;

  i.icon,
  svg {
    display: block;
    margin-left: 0;
  }
`;

const BreakdownHeading = styled.h3`
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const BreakdownHeader = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 72px 72px;
  padding: 10px 12px 0px;
  color: #111827;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

const BreakdownIconHeader = styled.div`
  width: 36px;
`;

const BreakdownCountHeader = styled.div`
  text-align: right;
`;

const BreakdownScoreHeader = styled.div`
  text-align: right;
`;

const BreakdownCount = styled.div`
  font-weight: 700;
  color: #1f2937;
  text-align: right;
  align-self: center;
`;

const BreakdownList = styled.div`
  display: grid;
  gap: 6px;
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 72px 72px;
  gap: 12px;
  margin-bottom: 2px;
  margin-left: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #f9f9fb;
  align-items: center;
`;

const BreakdownIconCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BreakdownText = styled.div`
  display: grid;
  gap: 0px;
`;

const BreakdownBadgeIcon = styled.span<{ color: string; borderColor: string; backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid ${props => props.borderColor};
  background: ${props => props.backgroundColor};
  color: ${props => props.color};

  i.icon,
  svg {
    width: 20px;
    height: 20px;
    font-size: 20px;
    margin-left: 0;
  }

`;

const BreakdownLabel = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const BreakdownDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const BreakdownPoints = styled.div`
  font-weight: 700;
  color: #111827;
  align-self: center;
  text-align: right;
  white-space: nowrap;
`;

const ChartSection = styled.div`
  margin-bottom: 24px;
`;

